import { z } from 'zod'
import {
  workflowSchema,
  workflowSessionIdSchema,
} from '@customTypes/cloudmix/workflow'
import {
  paginationSchema,
  timestampsSchema,
  createFilterableListQuerySchema,
  baseFilterBySchema,
  DEFAULT_ORDER,
} from './api'
import { mediaFormatSchema, serializeDimensions } from './common/mediaFormat'
import { sessionStatusSchema } from './sessions/status'
import { metadataSchema } from './common/metadata'

export const projectIdSchema = z.string().uuid()
export type ProjectId = z.infer<typeof projectIdSchema>

export const PROJECT_ORDER_BY_FIELDS = [
  'name',
  'createdAt',
  'lastUpdatedAt',
] as const

const projectDetailsSchema = z
  .object({
    projectId: projectIdSchema,
    name: z.string(),
    description: z.string(),
    mediaFormat: mediaFormatSchema,
    workflow: workflowSchema,
    defaultRegion: z.string(),
    status: sessionStatusSchema,
    workflowSessionId: workflowSessionIdSchema,
    defaultReleaseChannel: z.string().nullish(),
    defaultEnginePool: z.string().nullish(),
    metadata: metadataSchema,
  })
  .and(timestampsSchema)
export type ProjectDetails = z.infer<typeof projectDetailsSchema>

const listProjectsResponseSchema = z
  .object({
    items: z.array(projectDetailsSchema),
  })
  .and(paginationSchema)
export type ListProjectsResponse = z.infer<typeof listProjectsResponseSchema>

export const listProjectRequestSchema = createFilterableListQuerySchema(
  [...PROJECT_ORDER_BY_FIELDS],
  baseFilterBySchema,
  {
    orderBy: [DEFAULT_ORDER],
  }
)

export type ListProjectsRequest = z.infer<typeof listProjectRequestSchema>

export const createProjectSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  mediaFormat: mediaFormatSchema.default({
    fps: 25,
    dimensions: serializeDimensions({ width: 1920, height: 1080 }),
    sampleRate: 48000,
  }),
  defaultRegion: z.string().default('au-syd'),
  defaultReleaseChannel: z
    .string()
    .nullish()
    .transform((v) => v || null)
    .default(null),
  defaultEnginePool: z
    .string()
    .nullish()
    .transform((v) => v || null)
    .default(null),
  metadata: metadataSchema.optional(),
})
export type CreateProject = z.infer<typeof createProjectSchema>

const createProjectResponseSchema = z.object({
  projectId: projectIdSchema,
})
export type CreateProjectResponse = z.infer<typeof createProjectResponseSchema>

export const updateProjectSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  mediaFormat: mediaFormatSchema.default({
    fps: 25,
    dimensions: serializeDimensions({ width: 1920, height: 1080 }),
    sampleRate: 48000,
  }),
  defaultRegion: z.string().optional(),
  defaultReleaseChannel: z
    .string()
    .nullish()
    .transform((v) => v || null),
  defaultEnginePool: z
    .string()
    .nullish()
    .transform((v) => v || null),
  metadata: metadataSchema.optional(),
})
export type UpdateProject = z.infer<typeof updateProjectSchema>

const startProjectSchema = z.object({
  enginePool: z.string().optional(),
  region: z.string().optional(),
  minCpuCount: z.number().optional(),
  minGpuCount: z.number().optional(),
  engineId: z.string().uuid().optional(),
  engineVersion: z.string().optional(),
  mediasoupVersion: z.string().optional(),
})
export type StartProject = z.infer<typeof startProjectSchema>

export type WithProjectId<T> = T & { projectId: ProjectId }

export const getProjectLobbySchema = z.object({
  workflowSessionId: workflowSessionIdSchema,
  token: z.string(),
  controlStreamId: z.string(),
  hermesRouterAddress: z.string(),
})
export type GetProjectLobby = z.infer<typeof getProjectLobbySchema>
